import React, { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import Sidebar from "../../DesignSystem/Sidebar";
import SearchField from "../../DesignSystem/Inputs/SearchField";
import PrimaryButton from "../../DesignSystem/Buttons/PrimaryButton";
import SecondaryButton from "../../DesignSystem/Buttons/SecondaryButton";
import ProviderInput from "./components/ProviderInput";
import { useUserEmail } from "../../SignIn/UserEmailContext";
import { useNavigate } from "react-router-dom";
import {
  getProviderIssueReports,
  updateProviderIssueReport,
  PROVIDER_ISSUE_STATUS_OPTIONS,
} from "./utils/providerIssueReportsApi";
import "../../../css/ProviderNetwork/ProviderIssueReportsPage.css";
import ProviderSelectField from "./components/ProviderSelectField";
import Breadcrumb from "../../DesignSystem/Breadcrumb/Breadcrumb";

const issueTypeLabels = {
  missing_information: "Missing Information",
  incorrect_information: "Incorrect Information",
};

const statusLabels = {
  open: "Open",
  in_progress: "In Progress",
  resolved: "Resolved",
  closed: "Closed",
};

const formatDate = (value) => {
  if (!value) return "-";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return "-";

  return date.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const ProviderIssueReportsPage = () => {
  const navigate = useNavigate();
  const { userEmail } = useUserEmail();

  const [reports, setReports] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState("");

  const fetchReports = async () => {
    setLoading(true);

    try {
      const data = await getProviderIssueReports();

      setReports(data);

      const nextDrafts = data.reduce((acc, report) => {
        acc[report.id] = {
          status: report.status || "open",
          internalNotes: report.internalNotes || "",
        };

        return acc;
      }, {});

      setDrafts(nextDrafts);
    } catch (err) {
      console.error("[ProviderIssueReportsPage] Failed to load reports:", err);
      alert(err.message || "Failed to load provider issue reports.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReports();
  }, []);

  const filteredReports = useMemo(() => {
    const term = search.trim().toLowerCase();

    return reports.filter((report) => {
      const matchesSearch =
        !term ||
        report.providerName?.toLowerCase().includes(term) ||
        report.details?.toLowerCase().includes(term) ||
        report.reportedBy?.toLowerCase().includes(term) ||
        issueTypeLabels[report.issueType]?.toLowerCase().includes(term);

      const matchesStatus =
        statusFilter === "all" || report.status === statusFilter;

      return matchesSearch && matchesStatus;
    });
  }, [reports, search, statusFilter]);

  const updateDraft = (reportId, field, value) => {
    setDrafts((prev) => ({
      ...prev,
      [reportId]: {
        ...(prev[reportId] || {}),
        [field]: value,
      },
    }));
  };

  const saveReport = async (report) => {
    const draft = drafts[report.id];

    if (!draft) return;

    setSavingId(report.id);

    try {
      await updateProviderIssueReport({
        reportId: report.id,
        status: draft.status,
        internalNotes: draft.internalNotes,
        updatedBy: userEmail,
      });

      setReports((prev) =>
        prev.map((item) =>
          item.id === report.id
            ? {
                ...item,
                status: draft.status,
                internalNotes: draft.internalNotes,
                updatedBy: userEmail,
                updatedAt: new Date().toISOString(),
              }
            : item,
        ),
      );
    } catch (err) {
      console.error("[ProviderIssueReportsPage] Failed to save report:", err);
      alert(err.message || "Failed to save issue report.");
    } finally {
      setSavingId("");
    }
  };

  return (
    <div className="main-content">
      <Helmet>
        <title>Provider Issue Reports</title>
      </Helmet>

      <Sidebar />

      <div className="Main-Container">
        <div className="provider-issue-page">
          <Breadcrumb
            items={[
              {
                label: "Compliance",
                to: "/Department/Compliance",
              },
              {
                label: "Provider Index",
                to: "/provider-index",
              },
              { label: "Issue Reports" },
            ]}
          />
          <div className="provider-issue-header">
            <div>
              <div className="FP_H3 provider-issue-title">
                Provider Issue Reports
              </div>

              <div className="FP_Content black">
                View reported provider issues, update status and save internal
                notes.
              </div>
            </div>

            <div className="provider-issue-actions">
              <SearchField
                value={search}
                placeholder="Search provider, issue or reporter"
                onChange={(e) => setSearch(e.target.value)}
              />

              <ProviderSelectField
                value={statusFilter}
                onChange={setStatusFilter}
                options={[
                  { value: "all", label: "All Statuses" },
                  ...PROVIDER_ISSUE_STATUS_OPTIONS,
                ]}
              />

              <SecondaryButton
                type="button"
                label="Refresh"
                onClick={fetchReports}
              />
            </div>
          </div>

          <div className="provider-issue-summary">
            <SummaryCard label="Total" value={reports.length} />
            <SummaryCard
              label="Open"
              value={reports.filter((item) => item.status === "open").length}
            />
            <SummaryCard
              label="In Progress"
              value={
                reports.filter((item) => item.status === "in_progress").length
              }
            />
            <SummaryCard
              label="Resolved"
              value={
                reports.filter((item) => item.status === "resolved").length
              }
            />
          </div>

          {loading ? (
            <div className="provider-issue-empty">Loading requests...</div>
          ) : filteredReports.length ? (
            <div className="provider-issue-table-card">
              <table className="provider-issue-table">
                <thead>
                  <tr>
                    <th>Provider</th>
                    <th>Issue</th>
                    <th>Details</th>
                    <th>Status</th>
                    <th>Internal Notes</th>
                    <th>Action</th>
                  </tr>
                </thead>

                <tbody>
                  {filteredReports.map((report) => (
                    <IssueReportRow
                      key={report.id}
                      report={report}
                      draft={drafts[report.id] || {}}
                      saving={savingId === report.id}
                      onDraftChange={updateDraft}
                      onSave={() => saveReport(report)}
                      onOpenProvider={() => {
                        if (report.providerId) {
                          navigate(`/provider-index/${report.providerId}`);
                        }
                      }}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="provider-issue-empty">
              No provider issue reports found.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const SummaryCard = ({ label, value }) => {
  return (
    <div className="provider-issue-summary-card">
      <div className="FP_H3">{value}</div>
      <div className="FP_Help_Text">{label}</div>
    </div>
  );
};

const IssueReportRow = ({
  report,
  draft,
  saving,
  onDraftChange,
  onSave,
  onOpenProvider,
}) => {
  const currentStatus = draft.status || report.status || "open";

  return (
    <tr className={`provider-issue-row is-${currentStatus}`}>
      <td>
        <div className="provider-issue-provider-cell">
          <div className="FP_Content_Semibold black">
            {report.providerName || "Unknown Provider"}
          </div>

          {/* <span className={`provider-issue-status-pill is-${currentStatus}`}>
            {statusLabels[currentStatus] || "Open"}
          </span> */}

          {report.providerId && (
            <button
              type="button"
              className="provider-issue-link-btn"
              onClick={onOpenProvider}
            >
              View provider
            </button>
          )}
        </div>
      </td>

      <td>
        <div className="FP_Content black">
          {issueTypeLabels[report.issueType] || report.issueType || "-"}
        </div>
      </td>

      <td>
        <div className="provider-issue-details-stack">
          <div className="provider-issue-details-cell FP_Content black">
            {report.details || "-"}
          </div>

          <div className="provider-issue-detail-meta">
            <div>
              <span className="FP_Help_Text_Semibold">Reported By: </span>
              <span className="FP_Help_Text">{report.reportedBy || "-"}</span>
            </div>

            <div>
              <span className="FP_Help_Text_Semibold">Submitted: </span>
              <span className="FP_Help_Text">
                {formatDate(report.submittedAt || report.createdAt)}
              </span>
            </div>

            {report.updatedAt && (
              <div>
                <span className="FP_Help_Text_Semibold">Updated: </span>
                <span className="FP_Help_Text">
                  {formatDate(report.updatedAt)}
                </span>
              </div>
            )}

            {report.updatedBy && (
              <div>
                <span className="FP_Help_Text_Semibold">Updated By: </span>
                <span className="FP_Help_Text">{report.updatedBy}</span>
              </div>
            )}
          </div>
        </div>
      </td>

      <td>
        <ProviderSelectField
          value={currentStatus}
          options={PROVIDER_ISSUE_STATUS_OPTIONS}
          onChange={(value) => onDraftChange(report.id, "status", value)}
          className="provider-issue-table-select"
        />
      </td>

      <td>
        <ProviderInput
          type="textarea"
          rows={3}
          value={draft.internalNotes || ""}
          placeholder="Add notes"
          onChange={(value) => onDraftChange(report.id, "internalNotes", value)}
          className="provider-network-no-margin provider-issue-table-input"
        />
      </td>

      <td>
        <PrimaryButton
          type="button"
          label={saving ? "Saving..." : "Save"}
          onClick={onSave}
          disabled={saving}
        />
      </td>
    </tr>
  );
};
export default ProviderIssueReportsPage;
